import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Clock3,
  ExternalLink,
  FileText,
  MessageSquareText,
  Plus,
  Search,
  Target,
  Users,
  Workflow,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { getAdminClient, getAdminClients, getBusinessCenterProject, getBusinessCenterProjects, updateAdminClient, updateBusinessCenterProject } from "../../lib/api";
import type { BusinessCenterClientDetail, BusinessCenterProjectDetail, BusinessCenterProjectWorkspace, ClientRecord } from "../../lib/types";
import { loadTruevanceWorkspace } from "./TruevanceConnectApi";
import { TRUEVANCE_BRAND_NAME, TruevanceBrandHeader, useTruevanceBranding } from "./TruevanceBranding";
import { appendNoteText, collectTruevanceActivityFeed, type TruevanceActivityItem } from "./TruevanceCenterActivity";
import {
  archiveTruevanceSalesFollowUp,
  createTruevanceSalesFollowUp,
  loadTruevanceSalesWorkspace,
  updateTruevanceSalesFollowUp,
  updateTruevanceSalesLead,
  updateTruevanceSalesOpportunity,
} from "./TruevanceSalesCenterApi";
import type {
  BusinessFollowUpRecord,
  BusinessLeadRecord,
  BusinessOpportunityRecord,
  BusinessSalesWorkspacePayload,
} from "./TruevanceSalesCenterData";
import { calculateTruevanceMetrics, formatCurrency, formatDisplayDate, type TruevanceStore } from "./TruevanceConnectData";

type NoteScope = "lead" | "opportunity" | "client" | "project";
type ActivityFilter = "All" | "Lead" | "Discovery Call" | "Opportunity" | "Follow-up" | "Client" | "Project" | "Assessment" | "Recommendation" | "Roadmap";

interface CommunicationFollowUpForm {
  entityType: string;
  entityId: string;
  leadId: string | null;
  opportunityId: string | null;
  dueDate: string;
  priority: string;
  status: string;
  notes: string;
  completedAt: string;
}

function blankFollowUpForm(): CommunicationFollowUpForm {
  return {
    entityType: "lead",
    entityId: "",
    leadId: null,
    opportunityId: null,
    dueDate: new Date().toISOString().slice(0, 10),
    priority: "Medium",
    status: "Open",
    notes: "",
    completedAt: "",
  };
}

export function TruevanceCommunicationCenterPage() {
  const [salesWorkspace, setSalesWorkspace] = useState<BusinessSalesWorkspacePayload | null>(null);
  const [truevanceWorkspace, setTruevanceWorkspace] = useState<{ store: TruevanceStore } | null>(null);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [projects, setProjects] = useState<BusinessCenterProjectWorkspace | null>(null);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedClientDetail, setSelectedClientDetail] = useState<BusinessCenterClientDetail | null>(null);
  const [selectedProjectDetail, setSelectedProjectDetail] = useState<BusinessCenterProjectDetail | null>(null);
  const [noteScope, setNoteScope] = useState<NoteScope>("client");
  const [noteTargetId, setNoteTargetId] = useState("");
  const [noteTitle, setNoteTitle] = useState("Internal note");
  const [noteDetail, setNoteDetail] = useState("");
  const [noteMessage, setNoteMessage] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [followUpForm, setFollowUpForm] = useState<CommunicationFollowUpForm>(blankFollowUpForm());
  const [followUpEditingId, setFollowUpEditingId] = useState("");
  const [followUpMessage, setFollowUpMessage] = useState("");
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>("All");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useTruevanceBranding(
    "Communication Center",
    "Private consulting communications workspace for notes, history, follow-ups, email history, and client/project activity.",
  );

  const loadWorkspace = async () => {
    setLoading(true);
    setError("");
    try {
      const [sales, truevance, brandClients, brandProjects] = await Promise.all([
        loadTruevanceSalesWorkspace(),
        loadTruevanceWorkspace(),
        getAdminClients({ brand: "truevance_connect" }),
        getBusinessCenterProjects({ brand: "truevance_connect" }),
      ]);
      setSalesWorkspace(sales);
      setTruevanceWorkspace(truevance);
      setClients(brandClients);
      setProjects(brandProjects);
      setSelectedClientId((current) => current || brandClients[0]?.id || "");
      setSelectedProjectId((current) => current || brandProjects.projects[0]?.id || "");
      setNoteScope((current) => current || "client");
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load the communication workspace.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadWorkspace();
  }, []);

  useEffect(() => {
    if (!selectedClientId) {
      setSelectedClientDetail(null);
      return;
    }
    let cancelled = false;
    getAdminClient(selectedClientId)
      .then((detail) => {
        if (!cancelled) setSelectedClientDetail(detail);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : "Unable to load the selected client history.");
      });
    return () => {
      cancelled = true;
    };
  }, [selectedClientId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setSelectedProjectDetail(null);
      return;
    }
    let cancelled = false;
    getBusinessCenterProject(selectedProjectId)
      .then((detail) => {
        if (!cancelled) setSelectedProjectDetail(detail);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : "Unable to load the selected project history.");
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId]);

  const selectedLead = salesWorkspace?.leads.find((lead) => lead.id === noteTargetId && noteScope === "lead") || salesWorkspace?.leads[0] || null;
  const selectedOpportunity = salesWorkspace?.opportunities.find((opportunity) => opportunity.id === noteTargetId && noteScope === "opportunity") || salesWorkspace?.opportunities[0] || null;
  const selectedClient = clients.find((client) => client.id === (noteScope === "client" ? noteTargetId : selectedClientId)) || clients[0] || null;
  const selectedProject = projects?.projects.find((project) => project.id === (noteScope === "project" ? noteTargetId : selectedProjectId)) || projects?.projects[0] || null;

  useEffect(() => {
    if (noteScope === "lead") setNoteTargetId(salesWorkspace?.leads[0]?.id || "");
    if (noteScope === "opportunity") setNoteTargetId(salesWorkspace?.opportunities[0]?.id || "");
    if (noteScope === "client") setNoteTargetId(clients[0]?.id || "");
    if (noteScope === "project") setNoteTargetId(projects?.projects[0]?.id || "");
  }, [noteScope, clients, projects, salesWorkspace]);

  useEffect(() => {
    if (!followUpForm.entityId) {
      if (followUpForm.entityType === "lead") {
        setFollowUpForm((current) => ({ ...current, entityId: salesWorkspace?.leads[0]?.id || "", leadId: salesWorkspace?.leads[0]?.id || null }));
      } else if (followUpForm.entityType === "opportunity") {
        setFollowUpForm((current) => ({
          ...current,
          entityId: salesWorkspace?.opportunities[0]?.id || "",
          opportunityId: salesWorkspace?.opportunities[0]?.id || null,
        }));
      } else if (followUpForm.entityType === "client") {
        setFollowUpForm((current) => ({ ...current, entityId: clients[0]?.id || "", leadId: null, opportunityId: null }));
      } else if (followUpForm.entityType === "project") {
        setFollowUpForm((current) => ({ ...current, entityId: projects?.projects[0]?.id || "", leadId: null, opportunityId: null }));
      }
    }
  }, [clients, followUpForm.entityType, followUpForm.entityId, projects, salesWorkspace]);

  const activityFeed = useMemo(() => {
    const feed = collectTruevanceActivityFeed({
      salesWorkspace,
      truevanceStore: truevanceWorkspace?.store || null,
      clientDetail: selectedClientDetail,
      projectDetail: selectedProjectDetail,
    });
    const normalizedQuery = query.trim().toLowerCase();
    return feed.filter((item) => {
      const matchesFilter = activityFilter === "All" || item.source === activityFilter || item.kind === activityFilter.toLowerCase().replace(/\s+/g, "-");
      const haystack = `${item.source} ${item.title} ${item.detail}`.toLowerCase();
      return matchesFilter && (!normalizedQuery || haystack.includes(normalizedQuery));
    });
  }, [activityFilter, query, salesWorkspace, selectedClientDetail, selectedProjectDetail, truevanceWorkspace]);

  const followUps = salesWorkspace?.followUps || [];
  const openFollowUps = followUps.filter((item) => item.status !== "Archived");
  const dueFollowUps = openFollowUps.filter((item) => item.dueDate && item.dueDate.slice(0, 10) <= new Date().toISOString().slice(0, 10));
  const analytics = salesWorkspace?.metrics || null;
  const consultingMetrics = truevanceWorkspace ? calculateTruevanceMetrics(truevanceWorkspace.store) : null;

  const metrics = [
    {
      label: "Activity items",
      value: activityFeed.length,
      note: "Merged sales, client, project, and consulting history.",
    },
    {
      label: "Open follow-ups",
      value: openFollowUps.length,
      note: "Active reminders and next actions.",
    },
    {
      label: "Due today",
      value: dueFollowUps.length,
      note: "Follow-ups that need immediate attention.",
    },
    {
      label: "Consulting revenue",
      value: formatCurrency(consultingMetrics?.consultingRevenue || 0),
      note: "Revenue tracked in the Truevance operating layer.",
    },
  ];

  async function handleNoteSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!noteTargetId) {
      setNoteMessage("Choose a record before saving a note.");
      return;
    }
    if (!noteDetail.trim()) {
      setNoteMessage("Enter note details before saving.");
      return;
    }

    setNoteSaving(true);
    setNoteMessage("");
    try {
      if (noteScope === "lead" && selectedLead) {
        await updateTruevanceSalesLead(noteTargetId, {
          ...selectedLead,
          notes: appendNoteText(selectedLead.notes, noteTitle, noteDetail),
        });
      } else if (noteScope === "opportunity" && selectedOpportunity) {
        await updateTruevanceSalesOpportunity(noteTargetId, {
          ...selectedOpportunity,
          notes: appendNoteText(selectedOpportunity.notes, noteTitle, noteDetail),
        });
      } else if (noteScope === "client" && selectedClient) {
        await updateAdminClient(noteTargetId, {
          clientName: selectedClient.clientName,
          clientEmail: selectedClient.clientEmail,
          organizationName: selectedClient.organizationName || selectedClient.clientName,
          contactName: selectedClient.contactName || selectedClient.clientName,
          contactEmail: selectedClient.contactEmail || selectedClient.clientEmail,
          contactPhone: selectedClient.contactPhone || "",
          clientStatus: selectedClient.clientStatus || "active",
          clientNotes: appendNoteText(selectedClient.clientNotes, noteTitle, noteDetail),
          portalAccessStatus: selectedClient.portalAccessStatus || "preparation",
          portalAccessNotes: selectedClient.portalAccessNotes || "",
          futureProjectNotes: selectedClient.futureProjectNotes || "",
        });
      } else if (noteScope === "project" && selectedProject) {
        await updateBusinessCenterProject(noteTargetId, {
          clientName: selectedProject.clientName,
          projectName: selectedProject.projectName,
          projectType: selectedProject.projectType,
          projectStatus: selectedProject.projectStatus,
          projectPhase: selectedProject.projectPhase,
          paymentStatus: selectedProject.paymentStatus,
          assignedTeam: selectedProject.assignedTeam,
          assignedServices: selectedProject.assignedServices,
          milestones: selectedProject.milestones,
          deliverables: selectedProject.deliverables,
          completionPercent: selectedProject.completionPercent,
          revenueCents: selectedProject.revenueCents,
          startDate: selectedProject.startDate,
          dueDate: selectedProject.dueDate,
          portalAccessStatus: selectedProject.portalAccessStatus,
          portalAccessNotes: selectedProject.portalAccessNotes,
          notes: appendNoteText(selectedProject.notes, noteTitle, noteDetail),
          activityNote: `${noteTitle}: ${noteDetail}`,
        });
      }
      setNoteDetail("");
      setNoteMessage(`${noteScopeLabel(noteScope)} note saved successfully.`);
      await loadWorkspace();
    } catch (saveError) {
      setNoteMessage(saveError instanceof Error ? saveError.message : "Unable to save the note.");
    } finally {
      setNoteSaving(false);
    }
  }

  async function handleFollowUpSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!followUpForm.entityId) {
      setFollowUpMessage("Choose a follow-up target before saving.");
      return;
    }
    setFollowUpMessage("");
    try {
      if (followUpEditingId) {
        await updateTruevanceSalesFollowUp(followUpEditingId, followUpForm);
      } else {
        await createTruevanceSalesFollowUp(followUpForm);
      }
      setFollowUpForm(blankFollowUpForm());
      setFollowUpEditingId("");
      setFollowUpMessage(followUpEditingId ? "Follow-up updated." : "Follow-up created.");
      await loadWorkspace();
    } catch (saveError) {
      setFollowUpMessage(saveError instanceof Error ? saveError.message : "Unable to save the follow-up.");
    }
  }

  async function handleArchiveFollowUp(id: string) {
    await archiveTruevanceSalesFollowUp(id);
    await loadWorkspace();
  }

  const noteTargetOptions = useMemo(() => {
    if (noteScope === "lead") {
      return (salesWorkspace?.leads || []).map((lead) => ({ value: lead.id, label: `${lead.leadNumber} · ${lead.fullName || lead.organizationName || "Lead"}` }));
    }
    if (noteScope === "opportunity") {
      return (salesWorkspace?.opportunities || []).map((opportunity) => ({ value: opportunity.id, label: `${opportunity.opportunityNumber} · ${opportunity.opportunityName || opportunity.leadName || "Opportunity"}` }));
    }
    if (noteScope === "client") {
      return clients.map((client) => ({ value: client.id, label: `${client.clientName} · ${client.clientEmail}` }));
    }
    return (projects?.projects || []).map((project) => ({ value: project.id, label: `${project.projectNumber} · ${project.projectName || project.clientName || "Project"}` }));
  }, [clients, noteScope, projects, salesWorkspace]);

  const followUpTargetOptions = useMemo(() => {
    switch (followUpForm.entityType) {
      case "lead":
        return (salesWorkspace?.leads || []).map((lead) => ({ value: lead.id, label: `${lead.leadNumber} · ${lead.fullName || lead.organizationName || "Lead"}` }));
      case "opportunity":
        return (salesWorkspace?.opportunities || []).map((opportunity) => ({ value: opportunity.id, label: `${opportunity.opportunityNumber} · ${opportunity.opportunityName || opportunity.leadName || "Opportunity"}` }));
      case "client":
        return clients.map((client) => ({ value: client.id, label: `${client.clientName} · ${client.clientEmail}` }));
      case "project":
        return (projects?.projects || []).map((project) => ({ value: project.id, label: `${project.projectNumber} · ${project.projectName || project.clientName || "Project"}` }));
      default:
        return [];
    }
  }, [clients, followUpForm.entityType, projects, salesWorkspace]);

  if (loading) {
    return (
      <main className="truevance-shell truevance-business-shell min-h-screen text-[#F5F7FA]">
        <TruevanceBrandHeader />
        <section className="px-4 py-16 sm:px-6 lg:px-8">
          <div className="truevance-business-card truevance-business-card--dark mx-auto grid max-w-5xl gap-4 rounded-3xl p-8 sm:p-10">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#22E0D0]">{TRUEVANCE_BRAND_NAME}</p>
            <h1 className="font-display text-4xl font-bold text-[#F5F7FA]">Communication Center</h1>
            <p className="text-sm font-semibold leading-8 text-[#E8EEF4]">Loading client communication history, internal notes, follow-ups, and timeline activity.</p>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="rounded-2xl border border-[rgba(34,224,208,.12)] bg-white/5 p-4">
                  <div className="h-3 w-24 rounded-full bg-[rgba(34,224,208,.18)]" />
                  <div className="mt-4 h-8 w-20 rounded-full bg-[rgba(34,224,208,.12)]" />
                  <div className="mt-4 h-3 w-full rounded-full bg-[rgba(34,224,208,.08)]" />
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="truevance-shell truevance-business-shell min-h-screen text-[#F5F7FA]">
      <TruevanceBrandHeader />

      <section className="truevance-business-hero border-b border-[rgba(34,224,208,.16)] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[1.2fr_.8fr] lg:items-end">
          <div className="grid gap-4">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[rgba(34,224,208,.32)] bg-[rgba(7,26,47,.55)] px-3 py-2 text-xs font-black uppercase tracking-[0.22em] text-[#22E0D0]">
              <MessageSquareText className="size-4" aria-hidden="true" />
              {TRUEVANCE_BRAND_NAME}
            </div>
            <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">Communication Center</h1>
            <p className="max-w-3xl text-base font-semibold leading-8 text-[#E8EEF4]">
              Track notes, timeline activity, email history, discovery calls, and follow-ups across client and project workspaces without breaking brand separation.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/admin/truevance-business-center" className="admin-secondary-button focus-ring">
                <ArrowLeft className="size-4" aria-hidden="true" />
                Business Center Home
              </Link>
              <Link to="/admin/executive-dashboard" className="admin-secondary-button focus-ring">
                <ArrowRight className="size-4" aria-hidden="true" />
                Executive Dashboard
              </Link>
            </div>
          </div>

          <article className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#22E0D0]">Operational focus</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-[#F5F7FA]">History, notes, and next actions</h2>
            <p className="mt-3 text-sm font-semibold leading-7 text-[#E8EEF4]">
              The Business Center keeps communication visible so sales, delivery, and support stay aligned.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {metrics.map((metric) => (
                <MetricCard key={metric.label} {...metric} tone={metric.label === "Consulting revenue" ? "dark" : "light"} />
              ))}
            </div>
          </article>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8 xl:grid-cols-[.92fr_1.08fr]">
          <aside className="grid gap-6">
            <section className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
              <SectionHeading title="Internal notes" subtitle="Write a note to a lead, opportunity, client, or project without changing the overall workflow model." />
              <form onSubmit={handleNoteSubmit} className="mt-5 grid gap-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <SelectField label="Note scope" value={noteScope} onChange={(value) => setNoteScope(value as NoteScope)} options={["lead", "opportunity", "client", "project"]} />
                  <SelectField
                    label="Target record"
                    value={noteTargetId}
                    onChange={(value) => setNoteTargetId(value)}
                    options={noteTargetOptions.map((item) => item.value)}
                    labels={noteTargetOptions}
                  />
                </div>
                <TextField label="Note title" value={noteTitle} onChange={setNoteTitle} />
                <TextAreaField label="Note details" value={noteDetail} onChange={setNoteDetail} />
                <div className="flex flex-wrap justify-end gap-3">
                  <button type="submit" disabled={noteSaving} className="admin-primary-button focus-ring">
                    <Plus className="size-4" aria-hidden="true" />
                    {noteSaving ? "Saving..." : "Save note"}
                  </button>
                </div>
                {noteMessage ? <p className="text-sm font-semibold text-[#0F766E]">{noteMessage}</p> : null}
              </form>
            </section>

            <section className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
              <SectionHeading title="Follow-up management" subtitle="Create, edit, and archive reminders that keep the business moving." dark />
              <form onSubmit={handleFollowUpSubmit} className="mt-5 grid gap-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <SelectField label="Entity type" value={followUpForm.entityType} onChange={(value) => setFollowUpForm((current) => ({ ...current, entityType: value, entityId: "", leadId: value === "lead" ? null : current.leadId, opportunityId: value === "opportunity" ? null : current.opportunityId }))} options={["lead", "opportunity", "client", "project"]} dark />
                  <SelectField
                    label="Target record"
                    value={followUpForm.entityId}
                    onChange={(value) =>
                      setFollowUpForm((current) => ({
                        ...current,
                        entityId: value,
                        leadId: current.entityType === "lead" ? value : null,
                        opportunityId: current.entityType === "opportunity" ? value : null,
                      }))
                    }
                    options={followUpTargetOptions.map((item) => item.value)}
                    labels={followUpTargetOptions}
                    dark
                  />
                  <TextField label="Due date" type="date" value={followUpForm.dueDate} onChange={(value) => setFollowUpForm((current) => ({ ...current, dueDate: value }))} dark />
                  <SelectField label="Priority" value={followUpForm.priority} onChange={(value) => setFollowUpForm((current) => ({ ...current, priority: value }))} options={["Low", "Medium", "High"]} dark />
                  <TextField label="Status" value={followUpForm.status} onChange={(value) => setFollowUpForm((current) => ({ ...current, status: value }))} dark />
                  <TextField label="Completed at" type="datetime-local" value={followUpForm.completedAt} onChange={(value) => setFollowUpForm((current) => ({ ...current, completedAt: value }))} dark />
                </div>
                <TextAreaField label="Follow-up notes" value={followUpForm.notes} onChange={(value) => setFollowUpForm((current) => ({ ...current, notes: value }))} dark />
                <div className="flex flex-wrap justify-end gap-3">
                  <button type="submit" className="admin-primary-button focus-ring">
                    <Plus className="size-4" aria-hidden="true" />
                    {followUpEditingId ? "Update follow-up" : "Create follow-up"}
                  </button>
                </div>
                {followUpMessage ? <p className="text-sm font-semibold text-[#22E0D0]">{followUpMessage}</p> : null}
              </form>

              <div className="mt-6 grid gap-3">
                {followUps.length ? (
                  followUps.map((followUp) => (
                    <FollowUpCard
                      key={followUp.id}
                      record={followUp}
                      onEdit={() => {
                        setFollowUpEditingId(followUp.id);
                        setFollowUpForm({
                          entityType: followUp.entityType,
                          entityId: followUp.entityId,
                          leadId: followUp.leadId,
                          opportunityId: followUp.opportunityId,
                          dueDate: followUp.dueDate,
                          priority: followUp.priority,
                          status: followUp.status,
                          notes: followUp.notes,
                          completedAt: followUp.completedAt || "",
                        });
                      }}
                      onArchive={() => void handleArchiveFollowUp(followUp.id)}
                    />
                  ))
                ) : (
                  <EmptyState dark title="No follow-ups yet" description="Create reminders for client and project actions here." />
                )}
              </div>
            </section>
          </aside>

          <section className="grid gap-6">
            <section className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
              <div className="flex flex-col gap-4 border-b border-[rgba(7,26,47,.08)] pb-4 xl:flex-row xl:items-end xl:justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#17D4C4]">Global activity timeline</p>
                  <h2 className="mt-1 font-display text-3xl font-bold text-[#071A2F]">Operational history feed</h2>
                  <p className="mt-3 max-w-3xl text-sm font-semibold leading-7 text-[#334155]">
                    Combine sales, client, project, and Truevance consulting activity in one chronological workspace.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="grid gap-2 text-sm font-extrabold text-[#071A2F]">
                    Search
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#14B8A6]" aria-hidden="true" />
                      <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search notes, titles, or records" className="admin-input pl-11" />
                    </div>
                  </label>
                  <SelectField label="Filter" value={activityFilter} onChange={(value) => setActivityFilter(value as ActivityFilter)} options={["All", "Lead", "Discovery Call", "Opportunity", "Follow-up", "Client", "Project", "Assessment", "Recommendation", "Roadmap"]} />
                </div>
              </div>

              <div className="mt-5 grid gap-3">
                {activityFeed.length ? (
                  activityFeed.slice(0, 18).map((item) => (
                    <ActivityCard key={`${item.source}-${item.id}`} item={item} />
                  ))
                ) : (
                  <EmptyState title="No activity matches the current filter" description="Clear the filter or add new notes and follow-ups to populate the feed." />
                )}
              </div>
            </section>

            <section className="grid gap-6 xl:grid-cols-2">
              <HistoryPanel
                title="Client communication history"
                subtitle="Review notes, communication, and recent activity for the selected brand-scoped client."
                records={selectedClientDetail?.timeline.map((event) => ({
                  id: event.id,
                  source: "Client",
                  title: event.title,
                  detail: event.description,
                  date: event.createdAt,
                  relatedLink: event.relatedUrl || "/admin/truevance-client-center",
                  kind: "client" as const,
                })) || []}
                emptyTitle="No client history loaded"
                emptyDescription="Choose a client record to review notes, proposals, agreements, and payment history."
              />

              <HistoryPanel
                title="Project communication history"
                subtitle="Review project notes, milestones, and delivery history for the selected project."
                records={selectedProjectDetail?.timeline.map((event) => ({
                  id: event.id,
                  source: "Project",
                  title: event.title,
                  detail: event.detail,
                  date: event.createdAt,
                  relatedLink: event.relatedLink || `/admin/truevance-project-center/${selectedProjectDetail.project.id}`,
                  kind: "project" as const,
                })) || []}
                emptyTitle="No project history loaded"
                emptyDescription="Choose a project record to review delivery notes, milestones, and communication history."
              />
            </section>

            <section className="grid gap-6 xl:grid-cols-2">
              <HistoryPanel
                title="Email history and call logs"
                subtitle="Track discovery calls and message-driven activity from the sales and consulting workspaces."
                records={activityFeed.filter((item) => /email|call|proposal|welcome|follow-up/i.test(`${item.title} ${item.detail}`) || item.kind === "discovery-call")}
                emptyTitle="No email or call history matched"
                emptyDescription="The current filter did not find email or call touchpoints. Try another selection or add more activity."
              />

              <section className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
                <SectionHeading title="Reminder tracking and automation" subtitle="Keep visibility on due items and the next automations to wire later." dark />
                <div className="mt-5 grid gap-3">
                  {dueFollowUps.length ? (
                    dueFollowUps.slice(0, 6).map((followUp) => (
                      <div key={followUp.id} className="rounded-2xl border border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)] p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{followUp.followUpNumber}</p>
                            <h3 className="mt-1 font-display text-2xl font-bold text-[#F5F7FA]">{followUp.status}</h3>
                            <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{followUp.notes || "Follow-up item ready."}</p>
                          </div>
                          <span className="rounded-full border border-[rgba(214,178,74,.22)] px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-[#E0C96A]">
                            {formatDisplayDate(followUp.dueDate)}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <EmptyState dark title="No due reminders" description="Open follow-ups are up to date right now." />
                  )}
                </div>
                <div className="mt-6 rounded-2xl border border-[rgba(214,178,74,.18)] bg-[rgba(214,178,74,.08)] p-4">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#E0C96A]">Future automation triggers</p>
                  <ul className="mt-3 grid gap-2 text-sm font-semibold leading-7 text-[#E8EEF4]">
                    <li>Proposal accepted - create a client and project handoff.</li>
                    <li>Discovery completed - schedule the next follow-up reminder.</li>
                    <li>Project complete - request a testimonial and referral invitation.</li>
                    <li>Payment received - trigger onboarding and welcome workflows.</li>
                  </ul>
                </div>
              </section>
            </section>
          </section>
        </div>
      </section>
    </main>
  );
}

function ActivityCard({ item }: { item: TruevanceActivityItem }) {
  return (
    <article className="rounded-2xl border border-[rgba(7,26,47,.08)] bg-white p-4 transition hover:-translate-y-0.5 hover:border-[rgba(20,184,166,.32)] hover:shadow-[0_18px_40px_rgba(7,26,47,.08)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">{item.source}</p>
          <h3 className="mt-1 font-display text-2xl font-bold text-[#071A2F]">{item.title}</h3>
        </div>
        <span className="inline-flex items-center gap-2 rounded-full border border-[rgba(20,184,166,.16)] bg-[rgba(20,184,166,.08)] px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-[#0F766E]">
          <Clock3 className="size-3" aria-hidden="true" />
          {formatDisplayDate(item.date)}
        </span>
      </div>
      <p className="mt-3 text-sm font-semibold leading-7 text-[#334155]">{item.detail}</p>
      {item.relatedLink ? (
        <Link to={item.relatedLink} className="mt-4 inline-flex items-center gap-2 text-sm font-extrabold text-[#FF6A2B]">
          Open record
          <ExternalLink className="size-4" aria-hidden="true" />
        </Link>
      ) : null}
    </article>
  );
}

function HistoryPanel({
  title,
  subtitle,
  records,
  emptyTitle,
  emptyDescription,
}: {
  title: string;
  subtitle: string;
  records: TruevanceActivityItem[];
  emptyTitle: string;
  emptyDescription: string;
}) {
  return (
    <section className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
      <SectionHeading title={title} subtitle={subtitle} />
      <div className="mt-5 grid gap-3">
        {records.length ? (
          records.slice(0, 8).map((item) => <ActivityCard key={`${item.source}-${item.id}`} item={item} />)
        ) : (
          <EmptyState title={emptyTitle} description={emptyDescription} />
        )}
      </div>
    </section>
  );
}

function FollowUpCard({
  record,
  onEdit,
  onArchive,
}: {
  record: BusinessFollowUpRecord;
  onEdit: () => void;
  onArchive: () => void;
}) {
  return (
    <article className="rounded-2xl border border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{record.followUpNumber}</p>
          <h3 className="mt-1 font-display text-2xl font-bold text-[#F5F7FA]">{record.status}</h3>
          <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{record.notes || "Follow-up item ready."}</p>
        </div>
        <span className="rounded-full border border-[rgba(214,178,74,.22)] px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-[#E0C96A]">
          {record.priority}
        </span>
      </div>
      <div className="mt-3 grid gap-2 text-sm font-semibold text-[#E8EEF4]">
        <p>Entity: {record.entityType}</p>
        <p>Due: {formatDisplayDate(record.dueDate)}</p>
        <p>Completed: {record.completedAt ? formatDisplayDate(record.completedAt) : "Not completed"}</p>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" onClick={onEdit} className="admin-secondary-button focus-ring">
          Edit
        </button>
        <button type="button" onClick={onArchive} className="admin-secondary-button focus-ring">
          Archive
        </button>
      </div>
    </article>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  labels,
  dark = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  labels?: Array<{ value: string; label: string }>;
  dark?: boolean;
}) {
  const labelMap = new Map((labels || []).map((item) => [item.value, item.label]));
  return (
    <label className={`grid gap-2 text-sm font-extrabold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} className={`admin-input ${dark ? "admin-input--dark" : ""}`}>
        {options.map((option) => (
          <option key={option} value={option}>
            {labelMap.get(option) || option}
          </option>
        ))}
      </select>
    </label>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
  dark = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  dark?: boolean;
}) {
  return (
    <label className={`grid gap-2 text-sm font-extrabold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>
      {label}
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={`admin-input ${dark ? "admin-input--dark" : ""}`}
      />
    </label>
  );
}

function TextAreaField({
  label,
  value,
  onChange,
  dark = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  dark?: boolean;
}) {
  return (
    <label className={`grid gap-2 text-sm font-extrabold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>
      {label}
      <textarea value={value} onChange={(event) => onChange(event.target.value)} rows={4} className={`admin-input ${dark ? "admin-input--dark" : ""}`} />
    </label>
  );
}

function MetricCard({ label, value, note, tone }: { label: string; value: string | number; note: string; tone: "light" | "dark" }) {
  return (
    <article className={`rounded-2xl border ${tone === "dark" ? "border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.05)]" : "border-[rgba(7,26,47,.08)] bg-white"} p-4`}>
      <p className={`text-xs font-black uppercase tracking-[0.18em] ${tone === "dark" ? "text-[#22E0D0]" : "text-[#17D4C4]"}`}>{label}</p>
      <p className={`mt-2 font-display text-3xl font-bold ${tone === "dark" ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{value}</p>
      <p className={`mt-2 text-sm font-semibold leading-6 ${tone === "dark" ? "text-[#E8EEF4]" : "text-[#334155]"}`}>{note}</p>
    </article>
  );
}

function SectionHeading({ title, subtitle, dark = false }: { title: string; subtitle: string; dark?: boolean }) {
  return (
    <div className={`border-b pb-4 ${dark ? "border-[rgba(34,224,208,.16)]" : "border-[rgba(7,26,47,.08)]"}`}>
      <p className={`text-xs font-black uppercase tracking-[0.18em] ${dark ? "text-[#22E0D0]" : "text-[#17D4C4]"}`}>{TRUEVANCE_BRAND_NAME}</p>
      <h2 className={`mt-1 font-display text-3xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{title}</h2>
      <p className={`mt-3 max-w-4xl text-sm font-semibold leading-7 ${dark ? "text-[#E8EEF4]" : "text-[#334155]"}`}>{subtitle}</p>
    </div>
  );
}

function EmptyState({ title, description, dark = false }: { title: string; description: string; dark?: boolean }) {
  return (
    <div className={`rounded-2xl border px-4 py-5 ${dark ? "border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)]" : "border-[rgba(7,26,47,.08)] bg-[rgba(245,247,250,.92)]"}`}>
      <p className={`font-display text-2xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{title}</p>
      <p className={`mt-2 text-sm font-semibold leading-7 ${dark ? "text-[#E8EEF4]" : "text-[#334155]"}`}>{description}</p>
    </div>
  );
}

function noteScopeLabel(scope: NoteScope) {
  if (scope === "lead") return "Lead";
  if (scope === "opportunity") return "Opportunity";
  if (scope === "client") return "Client";
  return "Project";
}

